import dayjs from "dayjs"
import moment from "moment"
import { getAllWidgets, evaluateFormFieldComparator, foldScenarioCondition, formConditionsHandler, hasActiveSetAction, isConditionOrOperator, isValueEmpty, NUMBER_COMPARE_COMPARATORS, resolveConditionFieldLookup } from "./formsHelper.js";
import { getUserInfoByEmail } from "../services/WebServices.js";
import { getFormattedDate, getLocalDateFormatForWorkflow } from "./getLocalFormattedDate.js";
import { sortNodesByEdges } from "./workflowHelper.js";
import { safeDayjs } from "./safeDate.js";
import { formatWithThousandSeparators, stripThousandSeparators } from "./numberFormatter.js";

export const nodeConditionsHandler = (conditions, formData, elements = []) => {
  let data = formData, modCondition = conditions;
  let passedAction = [];

  // Built lazily and once per call rather than re-walking the element tree inside
  // the innermost condition-row loop. Same first-wins semantics as the .find() it
  // replaces. getAllWidgets itself is cached on the array identity.
  let widgetById = null
  const findWidgetById = (widgetId) => {
    if (!widgetById) {
      widgetById = new Map()
      getAllWidgets(elements).forEach(widget => {
        if (!widget?.id) return
        const key = String(widget.id)
        if (!widgetById.has(key)) widgetById.set(key, widget)
      })
    }
    return widgetId == null ? undefined : widgetById.get(String(widgetId))
  }

  for (let actionBlockIndex = 0; actionBlockIndex < modCondition.length; actionBlockIndex++) {
    const actionBlock = modCondition[actionBlockIndex];
    for (let blockIndex = 0; blockIndex < actionBlock?.scenarios.length; blockIndex++) {
      const conditionsBlock = actionBlock?.scenarios[blockIndex];
      let operator = conditionsBlock.operator;
      let finalResult = false
      let hasFolded = false
      for (let index = 0; index < conditionsBlock.conditions.length; index++) {
        const element = conditionsBlock.conditions[index];
        let foundWidget = findWidgetById(element.reference)
        if (!foundWidget && NUMBER_COMPARE_COMPARATORS.includes(element.comparator)) {
          foundWidget = { formType: "numberForm", multiSelect: null }
        }
        const lookup = resolveConditionFieldLookup(element.reference, data)
        const evaluation = evaluateFormFieldComparator({
          element,
          data,
          formType: foundWidget?.formType,
          foundWidget,
          currentValue: lookup.currentValue,
          lookupKey: lookup.lookupKey,
        })
        if (evaluation.skip) continue
        const result = !!evaluation.result
        finalResult = foldScenarioCondition(finalResult, result, operator, !hasFolded)
        hasFolded = true
        if (isConditionOrOperator(operator)) {
          if (finalResult) break
        } else if (!result) {
          break
        }

      }
      // Any passing scenario contributes its own action (approvers). Scenarios
      // on the same condition are OR — one match is enough.
      if (finalResult) {
        passedAction.push(conditionsBlock)
      }
    }
  }

  return passedAction

}

// `options.preserveHiddenValues` keeps a hidden field that still owns a value - one a
// condition writes into, or one that was filled in before it was hidden. Used for the
// document payload so a later condition can still read that value.
// `options.captureStepValues` is stricter and is what a step stores in formValues:
// every visible field (empty included) plus hidden fields a "set" writes. Other
// hidden fields are left out so they are not frozen on a later step.
// `options.valuesOnly` is the recursion into a hidden container.
export const getVisibleWidgets = (items, data, userInfo, userGroups, currentStatus = '', conditions, allItems, options = {}) => {
  const { preserveHiddenValues = false, valuesOnly = false, captureStepValues = false } = options
  let visiblWidgets = []
  items.map(item => {
    let proceed = true
    let keptForValue = false
    let isConditionallyHidden = false
    let rawActions = []
    if (conditions) {
      rawActions = formConditionsHandler(conditions, data, { ...item }, (allItems || items), currentStatus, userInfo, userGroups)
      let actions = rawActions.map(action => action.action)
      isConditionallyHidden = (item.hideInitially && (!actions || (actions && !actions.includes("show"))))
        || (actions && actions.includes("hide"))
      if (isConditionallyHidden) {
        proceed = false
      } else if (!captureStepValues && item.readOnly && (!actions || (actions && !actions.includes("editable"))) && !item.isUserWidget && !data[item.id]) {
        proceed = false
      } else if (!item.hide && !item.readOnly) {
        proceed = true
      }
      if (captureStepValues) {
        // Step formValues: every visible field, plus a hidden field only when a
        // "set" action writes into it. Other hidden fields stay off this step.
        if (isConditionallyHidden && hasActiveSetAction(rawActions, data)) {
          proceed = true
          keptForValue = true
        }
      } else if (preserveHiddenValues && (hasActiveSetAction(rawActions, data) || !isValueEmpty(data?.[item.id]))) {
        proceed = true
        keptForValue = true
      }
    } else if (!conditions || (conditions && conditions.length == 0)) {
      if (item.hideInitially || (!captureStepValues && item.readOnly && !data[item.id])) {
        proceed = false
      }
    }
    if (item.children && item.children.length > 0 && item.formType !== "tableForm") {
      if (proceed && !isConditionallyHidden) {
        visiblWidgets = [...visiblWidgets, ...getVisibleWidgets(item.children, data, userInfo, userGroups, currentStatus, conditions, (allItems || items), options)]
      } else if (preserveHiddenValues || captureStepValues) {
        visiblWidgets = [...visiblWidgets, ...getVisibleWidgets(item.children, data, userInfo, userGroups, currentStatus, conditions, (allItems || items), { ...options, valuesOnly: true })]
      }
    } else if (proceed && item.formType != "sectionForm" && item.formType != "infoForm" && (!valuesOnly || keptForValue)) {
      visiblWidgets.push(item)
    }
  })
  return visiblWidgets
}

// Values this step owns: visible fields (empty included) and hidden fields a
// condition sets. Other hidden fields are omitted so they are not frozen later.
export const getStepFormValues = (items, data, userInfo, userGroups, currentStatus, conditions, allItems) => {
  const widgets = getVisibleWidgets(items, data, userInfo, userGroups, currentStatus, conditions, allItems, { captureStepValues: true })
  const captured = {}
  widgets.forEach((element) => {
    if (!element?.id) return
    captured[element.id] = data?.[element.id] !== undefined && data?.[element.id] !== null ? data[element.id] : ""
  })
  return captured
}

const dateExistsInBetween = (startDate, endDate) => {
  let exists = false
  let start = safeDayjs(startDate)
  let end = safeDayjs(endDate)
  if (!start.isValid() || !end.isValid()) {
    return exists
  }
  let delegateStartDate = start.format('YYYY-MM-DD')
  let delegateEndDate = end.format('YYYY-MM-DD')
  let today = dayjs().format('YYYY-MM-DD')
  if (delegateStartDate <= today && today <= delegateEndDate) {
    exists = true
  }
  return exists
}

export const getRequiredApprovers = async (approvers, userInfo) => {
  let reqApprovers = [...approvers]
  for (let i = 0; i < approvers.length; i++) {
    let approver = approvers[i];
    if (approver === userInfo.email && userInfo.enableDelegation) {
      let validDate = dateExistsInBetween(userInfo.delegationStartDate, userInfo.delegationEndDate)
      if (validDate) {
        reqApprovers.push(userInfo.delegatedUser)
      }
      continue;
    }
    let infoResponse = await getUserInfoByEmail(approver)
    let approverInfo = infoResponse;
    if (approverInfo.enableDelegation) {
      let validDate = dateExistsInBetween(approver.delegationStartDate, approver.delegationEndDate)
      if (validDate) {
        reqApprovers.push(approverInfo.delegatedUser)
      }
    }
  }
  return reqApprovers
}

export const getRequestApprovers = (approvers, formData, spaceSpecificUsers = [], spaceSpecificGroups = [], previousValues = {}) => {
  let postApprovers = { user: [], group: [], role: [], userPicker: [] }
  if (Object.keys(approvers)?.length > 0) {
    for (const key in approvers) {
      if (key === "userPicker") {
        approvers[key].map(picker => {
          let pickerValuesInForm = getUserPickervalues(picker, formData, previousValues)
          if (!isObjectEmpty(pickerValuesInForm, ["user", "group"])) {
            let types = Object.keys(pickerValuesInForm)
            types.map(type => {
              if (type !== "userPicker" && type !== "role") {
                postApprovers[type] = Array.from(new Set([...postApprovers[type], ...pickerValuesInForm[type]]))
              }
            })
          } else {
            postApprovers[key] = Array.from(new Set([...postApprovers[key], picker]))
          }
        })
      } else if (key === "role") {
        approvers[key].map(role => {
          let roleUsers = spaceSpecificUsers?.filter(user => user.role === role && !user.restrictedUser).map(user => user.email)
          if (roleUsers.length > 0) {
            postApprovers["user"] = Array.from(new Set([...postApprovers["user"], ...roleUsers]))
          }
          let roleGroups = spaceSpecificGroups?.filter(group => group.role === group).map(group => group.groupId)
          if (roleGroups.length > 0) {
            postApprovers["group"] = Array.from(new Set([...postApprovers["group"], ...roleGroups]))
          }

        })
      } else {
        postApprovers[key] = Array.from(new Set([...postApprovers[key], ...approvers[key]]))
      }
    }
  }
  return postApprovers

}

export const getStepApprovers = (stepApprover = {}, formData, definedWidgets = [], spaceGroups, returnName = false, previousValues = {}) => {

  let approvers = []
  if (stepApprover && Object.keys(stepApprover)?.length > 0) {
    for (const key in stepApprover) {
      if (key === "user") {
        approvers = approvers.concat([...stepApprover.user])
      }
      if (key === "group") {
        if (stepApprover.group?.length > 0) {
          let groupNames = stepApprover.group
          if (returnName) {
            groupNames = stepApprover.group.map(group => {
              let groupObjInSpace = spaceGroups.find(grp => grp.groupId === group)
              let name = groupObjInSpace?.name || group
              return name
            })
          }
          approvers = approvers.concat([...groupNames])
        }

      }
      if (key === "userPicker") {
        stepApprover.userPicker.map(picker => {
          let pickerValuesInForm = getUserPickervalues(picker, formData, previousValues)

          if (!isObjectEmpty(pickerValuesInForm)) {
            let types = Object.keys(pickerValuesInForm)
            types.map(type => {
              if (type !== "userPicker" && type !== "role") {
                approvers = approvers.concat([...pickerValuesInForm[type]])
              }
            })
          } else {
            let pickerLabel = definedWidgets.find(widget => widget.id === picker)
            if (pickerLabel) {
              approvers = approvers.concat([pickerLabel?.label])
            }
          }
        })
      }
    }
  }
  return approvers
}

// Resolve a user-picker widget's selected users/groups. The current form's
// value wins. `previousValues` is only a fallback for pickers filled on an
// earlier step (public/ongoing payloads) and must not be treated as this
// step's form values.
export const getUserPickervalues = (picker, formData, previousValues) => {
  const raw = formData?.[picker] || previousValues?.[picker]
  if (!raw || typeof raw !== "object") {
    return {}
  }
  const pickerResult = { ...raw }
  if (pickerResult.dropdownItem) {
    delete pickerResult.dropdownItem
  }
  return pickerResult
}

export const isObjectEmpty = (obj, checkList) => {
  if (!obj) {
    return true
  } else if (Object.keys(obj).length == 0) {
    return true
  } else {
    let types = checkList || Object.keys(obj)
    let isEmpty = true
    for (let index = 0; index < types.length; index++) {
      const element = types[index];
      if (obj[element] && obj[element].length > 0) {
        isEmpty = false
        break
      }
    }
    return isEmpty
  }
}

// Build the workflow entry for an approval step that was bypassed because no
// approvers could be resolved for it. Shared by the request submit, approval and
// public form flows so a skipped step is recorded the same way wherever the skip
// happens, and the timeline/print renders the same "skipped" row.
export const buildSkippedStepData = (node) => ({
  formValues: {},
  ...node?.data,
  dueDate: "",
  comment: "",
  name: node?.data?.name,
  count: node?.data?.count,
  approvalStep: node?.data?.approvalStep,
  period: node?.data?.period,
  assignTo: node?.data?.assignTo,
  formId: node?.formData?.formId,
  approvalStatus: "skipped",
  delayed: false,
  actionBy: "",
  actionOn: ""
})

export const isUserApprover = (userInfo, approvers, userGroups = [], allowAdmin = true) => {
  let result = false, groupList = [...userGroups]
  // let _approverRecord = _modApproverRecord && _modApproverRecord[stepName] ? { ..._modApproverRecord[stepName], approvers: _modApproverRecord[stepName][stepNumber == completeCardList.length - 1 ? "approver_approvers" : "last_step_approver_approvers"] } : { ...approverRecord }
  let curUserEmail = userInfo.email
  if (userInfo.isAdmin && allowAdmin) {
    result = true
  } else {
    if (approvers) {
      let types = Object.keys(approvers)
      for (let index = 0; index < types.length; index++) {
        const type = types[index];
        if (type == "user") {
          let userEmails = [curUserEmail]
          if (userInfo.aliases) {
            userEmails = userEmails.concat(userInfo.aliases)
          }
          let curUser = userEmails.find(g => approvers[type].includes(g))
          if (curUser) {
            result = { user: curUserEmail, type: "user", typeValue: curUserEmail }
            break
          }

        } else if (type == "group") {
          let curTypeApprovers = approvers[type]
          if (curTypeApprovers?.length == 0) {
            continue
          }
          let curGroup = groupList.filter(g => curTypeApprovers.includes(g.groupId))
          if (curGroup && curGroup.length > 0) {
            let temp = curGroup.map(c => c.email)
            temp = Array.from(new Set(temp))
            result = { user: curUserEmail, type: "group", typeValue: temp }
            if (temp.length === approvers[type].length) {
              break
            } else {
              let groupNotInCurrentUser = approvers[type].filter(g => !temp.includes(g))
              groupNotInCurrentUser = Array.from(new Set(groupNotInCurrentUser))
              result = { ...result, typeValue: [...temp, ...groupNotInCurrentUser] }
              break
            }
          }
        }
      }
    } else {
      result = null
    }
  }
  return result
}

export const formatViewSelectedColumns = (selectedColumns, defaultViewColumns) => {
  let reqCols = [];
  selectedColumns.forEach(col => {
    let colObj = defaultViewColumns.find(column => column.id === col)
    reqCols.push({ ...colObj })
  })
  return reqCols
}

export const getFormattedRequestValues = (value, users = [], groups = [], pickers = [], items = []) => {
  let reqValues = {}
  const itemsById = new Map((items || []).map(item => [item?.id, item]))

  const getObjectLabelValue = (obj) => {
    if (!obj || typeof obj !== "object") return ""
    const name = (typeof obj.name === "string" ? obj.name : "").trim()
    const label = (typeof obj.label === "string" ? obj.label : "").trim()
    const title = (typeof obj.title === "string" ? obj.title : "").trim()
    const link = (typeof obj.link === "string" ? obj.link : "").trim()

    if (name && label) return `${name} - ${label}`
    if (name) return name
    if (label) return label
    if (title) return title
    if (link) return link
    return ""
  }

  const formatValueByWidget = (rawValue, widget) => {
    if (rawValue === null || rawValue === undefined || rawValue === "") return ""

    if (Array.isArray(rawValue)) {
      if (widget?.multiSelect || widget?.formType === "multipleChoiceField") {
        const mapped = rawValue.map(val => {
          const currentOpt = widget?.options?.find(opt => opt.value === val)
          return currentOpt?.label || val
        })
        return mapped.filter(Boolean).join(", ")
      }

      const mapped = rawValue.map(val => {
        if (typeof val === "object") {
          return getObjectLabelValue(val)
        }
        return val
      })
      return mapped.filter(Boolean).join(" | ")
    }

    if (typeof rawValue === "object" && (rawValue?.user || rawValue?.group || rawValue?.userPicker)) {
      return getMergedPickerValues(rawValue, users, groups, pickers)
    }

    if (rawValue?.$isDayjsObject || isValidDate(rawValue)) {
      return getFormattedDate(rawValue)
    }

    if (typeof rawValue === "object") {
      return getObjectLabelValue(rawValue)
    }

    let currentValue = rawValue
    if ((widget?.formType === "dropdownField" || widget?.formType === "radioField") && widget?.options) {
      if (!widget.multiSelect) {
        const currentOpt = widget.options.find(opt => opt.value === currentValue)
        currentValue = currentOpt?.label || currentValue
      }
    }
    if (widget?.formType === "numberForm") {
      const stripped = stripThousandSeparators(currentValue)
      if (stripped !== "" && Number.isFinite(Number(stripped))) {
        currentValue = formatWithThousandSeparators(currentValue)
      }
    }
    if (currentValue === true) {
      currentValue = "√"
    }
    return currentValue
  }

  const tableColumnWidgets = (items || []).filter(item => item?.parentTableId && typeof item?.id === "string" && item.id.includes("::"))
  tableColumnWidgets.forEach(widget => {
    const [tableId, columnId] = widget.id.split("::")
    const tableRows = value?.[tableId]
    if (!Array.isArray(tableRows)) {
      reqValues[widget.id] = ""
      if (reqValues[columnId] === undefined) {
        reqValues[columnId] = ""
      }
      return
    }

    const mergedValues = tableRows
      .map(row => {
        if (!row || typeof row !== "object" || Array.isArray(row)) return ""
        const tableColDef = itemsById.get(widget.id)
        return formatValueByWidget(row[columnId], tableColDef)
      })
      .filter(Boolean)

    const mergedText = Array.from(new Set(mergedValues)).join("\n")
    reqValues[widget.id] = mergedText
    if (reqValues[columnId] === undefined || reqValues[columnId] === "") {
      reqValues[columnId] = mergedText
    }
  })

  Object.keys(value).forEach(key => {
    let currentValue = value[key] ?? ""
    let curItem = itemsById.get(key)
    reqValues[key] = formatValueByWidget(currentValue, curItem)
  })
  return reqValues
}

const getMergedPickerValues = (picker, spaceUsers, spaceGroups, spacePickers) => {
  let userWidgetValues = [];
  if (typeof picker === "object" && (picker.user || picker.group || picker.userPicker)) {
    if (picker.user?.length > 0) {
      let userArr = picker.user.map(email => {
        let userInfo = spaceUsers.find(user => user.email === email)
        return userInfo?.name || email
      })
      userWidgetValues = userWidgetValues.concat([...userArr])
    }
    if (picker.group?.length > 0) {
      let groupArr = picker.group.map(groupId => {
        let groupInfo = spaceGroups.find(group => group.groupId === groupId)
        return groupInfo?.name || groupId
      })
      userWidgetValues = userWidgetValues.concat([...groupArr])
    }
    if (picker.userPicker?.length > 0) {
      let pickerArr = picker.userPicker.map(pickerId => {
        let pickerInfo = spacePickers.find(picker => picker.id === pickerId)
        return pickerInfo?.name || pickerId
      })
      userWidgetValues = userWidgetValues.concat([...pickerArr])
    }
  }

  return userWidgetValues.join(" | ")
}

export const isValidDate = (dateString) => {
  const utcDateRegex = /\b\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(\.\d{3})?Z\b/;
  return utcDateRegex.test(dateString)
}
export const isApprovalStep = (approvers, userInfo, userGroups = [], workflow) => {
  let isApprover = false;
  if (approvers?.user?.includes(userInfo.email) || userInfo.isAdmin) {
    isApprover = true
  }
  let curUserGroups = userGroups.map(grp => grp.groupId)
  let userGroupInApprovers = curUserGroups.filter(g => approvers?.group?.includes(g))
  if (userGroupInApprovers?.length > 0) {
    isApprover = true;
  }
  return isApprover;
}

export const getUniqueStepUsers = (approvers, spaceGroups = []) => {
  let users = [], groups = []
  if (approvers) {
    for (const key in approvers) {
      if (key === 'user') {
        users = users.concat(approvers["user"])
      }
      if (key === "group") {
        let members = []
        approvers.group.map(grp => {
          let grpInfo = spaceGroups.find(group => group.groupId === grp)
          if (grpInfo) {
            members = members.concat(grpInfo.users?.map(user => user.email))
          }
        })
        users = users.concat([...members])
      }
    }
  }
  return { user: Array.from(new Set([...users])), group: Array.from(new Set([...groups])) };
}

export const getDifferenceValues = (formItems, formValues, userInfo, userGroups, currentStatus, formConditions, existingValues) => {

  // A field that owns a value is part of the step's values whether or not it is on
  // screen, so hidden fields holding one are collected too.
  let formWidgetList = getVisibleWidgets(formItems, { ...formValues }, userInfo, userGroups, currentStatus, formConditions, undefined, { preserveHiddenValues: true })
  let modcurrentDefaultValues = {}, difference = {};

  for (let index = 0; index < formWidgetList.length; index++) {
    const element = formWidgetList[index];
    let currentValue = formValues && formValues[element.id]
    // formValues && formValues[element.id] !== undefined ? formValues[element.id] : ''
    // if (element.formType === "attachment" && Array.isArray(currentValue)) {
    //   currentValue = currentValue.map(c => {
    //     return { uuid: c.uuid, name: c.name }
    //   })
    // }
    modcurrentDefaultValues = {
      ...modcurrentDefaultValues,
      [element.id]: currentValue
    }
  }
  
  if (existingValues) {
    let preValues = { ...existingValues }
    
    // Check all keys in current values for changes
    for (const key in modcurrentDefaultValues) {
      const newValue = modcurrentDefaultValues[key]
      const oldValue = preValues[key]
      let hasChanged = false
      if(preValues.hasOwnProperty(key) && !newValue){
        hasChanged = false
      } else if(!preValues.hasOwnProperty(key) && modcurrentDefaultValues.hasOwnProperty(key)){
        hasChanged = true
      } else {  
        hasChanged  = compareValues(oldValue, newValue)
      }
      
      if (hasChanged) {
        // Treat undefined, null, or empty string as empty string in difference
        const valueToAssign = (newValue === undefined || newValue === null || newValue === '') ? "" : newValue
        difference = { ...difference, [key]: valueToAssign }
      }
    }
    
    // Check for keys that existed in old values but are not in new values
    for (const key in preValues) {
      if (!modcurrentDefaultValues.hasOwnProperty(key)) {
        let keyInVisibleWidgets = formWidgetList.find(widget => widget.id === key)
        if(keyInVisibleWidgets){
          difference = { ...difference, [key]: "" }
        }
      }
    }
  } else {
    difference = modcurrentDefaultValues
  }
  return difference;
}

const compareValues = (oldValue, newValue) => {
  // Helper function to detect if a value is a date
  const isDateValue = (val) => {
    if (val === undefined || val === null) return false
    if (typeof val === 'object' && val.$isDayjsObject) return true
    if (typeof val === 'string' && isNaN(Number(val)) && safeDayjs(val).isValid()) return true
    return false
  }

  if(oldValue === undefined && newValue === "" || oldValue === "" && newValue === undefined) {
    return false;
  }

  if(newValue === undefined) {
    return true;
  }

  // Helper function to check if value is "empty" (undefined, null, or empty string)
  const isEmpty = (val) => {
    return val === undefined || val === null || val === ''
  }

  // If both are empty states, they are considered the same (no difference)
  if (isEmpty(oldValue) && isEmpty(newValue)) {
    return false
  }

  // Both are dates - use date comparison
  if (isDateValue(oldValue) && isDateValue(newValue)) {
    return !areDatesSame(new Date(oldValue), new Date(newValue))
  }
  
  // Both are objects/arrays (but not dates) - use deep comparison
  if (typeof oldValue === 'object' && oldValue !== null && typeof newValue === 'object' && newValue !== null) {
    return !areObjectsEqual(oldValue, newValue)
  }
  
  // Primitive values - direct comparison (including undefined and null)
  return oldValue !== newValue
}

export const areDatesSame = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export const areObjectsEqual = (obj1, obj2) => {
  if (obj1 === obj2) {
    return true;
  }

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (!keys2.includes(key) || !areObjectsEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export const hideApprovalStatus = ["Approved", "approved", "rejected", "refused", "draft", "archived", "deleted", "closed"]
export const getShowApprovalTab = (requestDetail, userInfo, userGroups) => {
  let show = false;
  let endStep = requestDetail?.workflow?.find(step => step.type === "end") || null
  if (!endStep && requestDetail?.workflow?.length > 0 && requestDetail?.requestId && (!hideApprovalStatus.includes(requestDetail?.status))) {
    let currentStepApprovers = requestDetail.approvers;
    let currentStep = requestDetail.workflow.find(step => step.name === requestDetail.status)
    if (currentStep?.delegatedUser?.length > 0) {
      currentStepApprovers.user = currentStepApprovers.user?.concat([...currentStep.delegatedUser])
    }
    show = isApprovalStep(currentStepApprovers, userInfo, userGroups)
    if(currentStep?.collaborators && currentStep.collaborators?.user?.includes(userInfo.email)) {
      show = true
    }
  }
  return show
}

// True when the signed-in user has already submitted, approved, or refused the
// current workflow step. Submit is hidden in that case; Save and field editing
// should follow the same rule, even if other assignees still need to act.
export const hasUserActedOnCurrentStep = (requestDetail, userInfo) => {
  if (!requestDetail?.workflow?.length || !userInfo?.email) {
    return false
  }
  const currentStep = requestDetail.workflow.find((step) => step.name === requestDetail.status)
  const statuses = currentStep?.statuses || []
  return statuses.some((status) => status.actionBy === userInfo.email)
}

export const getApproversWithDelegated = (approvers, workflow, status) => {
  let reqApprovers = { user: approvers.user || [], group: approvers.group || [] }
  let currentStep = workflow.find(step => step.name === status)
  // if (currentStep?.delegatedUser?.length > 0) {
  //   reqApprovers.user = reqApprovers.user?.concat([...currentStep.delegatedUser])
  // }
  return {user: Array.from(new Set(reqApprovers.user)), group: Array.from(new Set(reqApprovers.group))}
}

export const getShowDuplicateCheckTab = (requestDetail) => {
  let show = false, editable = false;
  let workflow = requestDetail?.originalWorkflow, nodes = workflow?.nodes || [];
  let isAvailable = nodes.find(node => node.data?.type === "duplicateCheck")

  if (!isAvailable) {
    return false
  }
  let nodeIndex = nodes.findIndex(node => node.data?.name === requestDetail?.status)
  if (nodeIndex !== -1) {
    let duplicateNodeIndex = nodes.findIndex(node => node.data?.type === "duplicateCheck")
    if (duplicateNodeIndex < nodeIndex) {
      show = true
    }
  }
  return show
}

export const getShowRequesterSaveButton = (requesterNode, defaultRequestDetail, userInfo ) => {
  return requesterNode?.data?.name === defaultRequestDetail?.status && Number(defaultRequestDetail?.currentCycle) > 1 && (userInfo.isAdmin ||  defaultRequestDetail?.createdBy?.email === userInfo.email);
}

export const valueModifier = (_values) => {
  let result = {}
  let values = { ..._values } || {}
  if (typeof _values == "string") {
    try {
      values = JSON.parse(_values)
      if (typeof values != "object") {
        values = {}
      }
    } catch (error) {

    }
  }
  Object.keys(values).map(key => {

    let isDate = isValidDate(values[key]);
    //  values[key] ? moment(values[key], formats, true)?.isValid() : false
    if (values[key] && isDate) {
      result = { ...result, [key]: safeDayjs(values[key]) }
    } else if (Array.isArray(values[key])) {
      let arrValue = values[key]
      arrValue = arrValue.map((value, index) => {
        let reqData = {}
        if (typeof value !== "string") {
          for (const key in value) {
            let currentValue = value[key]
            // Table cells: a rehydrated dayjs still reports $isDayjsObject but has
            // no methods, so it has to be rebuilt rather than passed to dayjs().
            if (currentValue?.$isDayjsObject || isValidDate(currentValue)) {
              currentValue = safeDayjs(currentValue)
            }
            reqData[key] = currentValue
          }
          return { ...reqData, key: index }
        } else {
          return value
        }
      })
      result = { ...result, [key]: arrValue }
    } else if (values[key] && !isDate) {
      result = { ...result, [key]: values[key] }
    }
  })
  return result
}

export const getNotificationRecipients = (workflow, userEmail) => {
  let reqUsers = [], groups = [];
  if (workflow?.length > 0) {
    workflow.map(step => {
      if (step.type === "default") {
        reqUsers = reqUsers.concat(step.actionBy)
      } else {
        let users = step.assignTo?.user || []
        reqUsers = reqUsers.concat(users)
        groups = groups.concat(step.assignTo?.group || [])
      }
    })
  }
  reqUsers = Array.from(new Set(reqUsers)).filter(u => u !== userEmail);
  groups = Array.from(new Set(groups));
  return { user: reqUsers, group: groups };
}

export const buildWorkflowActionPayload = ({
  requestId,
  approvedStatus,
  formValues = {},
  value = {},
  comment = "",
  actionBy = "",
}) => {
  const promotesValue = approvedStatus === "approved" || approvedStatus === "submitted"

  return {
    formValues: { ...formValues },
    value: promotesValue ? { ...value } : {},
    approvedStatus,
    comment,
    actionBy,
    actionOn: moment().utc(),
    // Misspelling is the API contract, not a typo on this side.
    reqeustId: requestId,
  }
}

// WORKFLOW_STEP_WAITING (and its due-date variant) tells the next step's approvers
// that a request is waiting on their approval. A step that only collects a form
// submission has nothing to approve, so the waiting mail is suppressed for it.
// Any other notification type passes through untouched.
export const canSendStepWaitingNotification = (template, nextStep) => {
  const waitingTypes = ["WORKFLOW_STEP_WAITING", "WORKFLOW_STEP_WAITING_WITH_DUE_DATE"]
  if (!waitingTypes.includes(template?.notificationType)) {
    return true
  }
  return Boolean(nextStep?.data?.approvalStep || nextStep?.approvalStep)
}
